import { useWatch } from "react-hook-form";
import React, { useMemo, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";

import { useGetShipmentInvoices } from "api/queries/invoice/useGetShipmentInvoices";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import { getProviderCostItems } from "api";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import { COST_REGEX } from "helpers/costHelpers";
import FormField from "components/molecules/FormField/FormField";
import { weightUnitOptions } from "constants/Common";
import TotalCost from "./components/TotalCost";
import CostTable from "./components/CostTable";
import { ICostItem } from "./InvoiceFormCostTypes";

const initialInvoiceData: any = [];

function divider() {
  const hrStyle = {
    marginLeft: "-15px",
    marginRight: "-15px",
    height: 6,
    backgroundColor: "#F7F9FA",
    border: "none",
  };
  return <hr style={hrStyle} />;
}

function InvoiceFormCosts({ invoiceConfig }: any) {
  const { id: shipmentId }: any = useParams();
  const [searchParams] = useSearchParams();
  const currentInvoiceId = searchParams.get("invoice");
  const costData = useWatch({ name: "costs" });
  const intl = useIntl();
  const [costItems, setCostItems] = useState<ICostItem[]>([]);

  const { data: invoices = initialInvoiceData } = useGetShipmentInvoices({
    shipmentId: shipmentId!,
    options: { select: (data: any) => data.results },
  });
  const { data: shipmentData } = useGetShipmentBasic(shipmentId, {
    refetchOnMount: false,
  });

  const currentInvoice = useMemo(
    () => invoices.find((invoice: any) => invoice.invoice_id === currentInvoiceId),
    [currentInvoiceId, invoices],
  );

  const isCustomsBrokerInvoice = currentInvoice?.invoice_type === "customs_broker_invoice";

  const { actual_total_weight, actual_total_weight_uom } = currentInvoice?.good_items || {};

  useEffect(() => {
    if (!shipmentData || costItems.length) return;
    getProviderCostItems(shipmentData?.shipment.quotation_ref).then((res: any) => {
      setCostItems(res.data);
    });
  }, [shipmentData]);

  if (!costData) return <LoadingSkeleton rows={1} />;

  // Backend sends the costs in a random order, so we sort them here
  const predefinedOrder = ["main_freight", "origin", "destination"];
  const sortedEntries = Object.entries(costData).sort(
    ([keyA], [keyB]) => predefinedOrder.indexOf(keyA) - predefinedOrder.indexOf(keyB),
  );

  return (
    <div className="invoice-form-cost">
      <h5 className="mb-3">
        <FormattedMessage id="quotation-form.costs" defaultMessage="Costs" />
      </h5>
      <table className="cost-table chargeable-weight">
        <thead>
          <tr>
            <th aria-label="Column header" />
            <th>
              <FormattedMessage id="billing.invoiceForm.quotedWeight" defaultMessage="Quoted Weight" />
            </th>
            <th>
              <FormattedMessage id="billing.invoiceForm.actualWeight" defaultMessage="Actual Weight" />
            </th>
            <th>
              <FormattedMessage id="new-request.packing-list.unit" defaultMessage="Unit" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <FormattedMessage id="quotation-form.chargeableWeight" defaultMessage="Chargeable weight" />
            </td>
            <td>
              {actual_total_weight} {actual_total_weight_uom}
            </td>
            <td>
              {!isCustomsBrokerInvoice && (
                <div className="w-92-px">
                  <FormField
                    field_id="costs.chargeable_weight.value"
                    placeholder={intl.formatMessage({
                      id: "quotation-form.enterWeight",
                      defaultMessage: "Enter weight",
                    })}
                    validations={{
                      shouldUnregister: true,
                    }}
                    validationPattern={{
                      value: COST_REGEX,
                      message: intl.formatMessage({
                        id: "general.requiredField",
                        defaultMessage: "This is a required field.",
                      }),
                    }}
                    mandatory
                  />
                </div>
              )}
            </td>
            <td>
              <div className="w-92-px">
                <Dropdown
                  useHookForm
                  name="costs.chargeable_weight.unit"
                  defaultValue={weightUnitOptions[0].value}
                  options={weightUnitOptions}
                  required
                  shouldUnregister
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {sortedEntries.map(([key]: any) => {
        if (key !== "destination" && key !== "origin" && key !== "main_freight") return null;
        return (
          <div key={key} className="mt-5">
            <CostTable
              key={key}
              title={key}
              currentInvoice={currentInvoice}
              costItems={costItems}
              invoiceConfig={invoiceConfig}
            />
            {divider()}
          </div>
        );
      })}
      <TotalCost isCustomBroker={currentInvoice?.invoice_type === "customs_broker_invoice"} />
    </div>
  );
}

export default InvoiceFormCosts;
